// 生产环境下后端同源提供API，开发环境用 Vite proxy 转发到 :3000
const API_BASE_URL = '/api';

// 通用的API请求函数
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  // 获取token
  const token = localStorage.getItem('auth_token');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API请求失败: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API请求错误:', error);
    throw error;
  }
}

// 联系方式API
export const contactApi = {
  // 获取所有联系方式（公开）
  getContactInfos: (type?: string) => 
    apiRequest<{ success: boolean; data: { contactInfos: any[] } }>(
      `/contacts/info${type ? `?type=${type}` : ''}`
    ),

  // 获取所有联系方式（管理员）
  getAllContactInfos: (type?: string, isActive?: boolean) => 
    apiRequest<{ success: boolean; data: { contactInfos: any[]; types: string[] } }>(
      `/contacts/infos/all?${new URLSearchParams({
        ...(type && { type }),
        ...(isActive !== undefined && { isActive: isActive.toString() })
      }).toString()}`
    ),

  // 创建联系方式（管理员）
  createContactInfo: (data: {
    type: string;
    nameZh: string;
    nameVi: string;
    value: string;
    displayOrder?: number;
    icon?: string;
    isActive?: boolean;
    descriptionZh?: string;
    descriptionVi?: string;
  }) => 
    apiRequest<{ success: boolean; data: { contactInfo: any } }>('/contacts/infos', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 更新联系方式（管理员）
  updateContactInfo: (id: number, data: Partial<{
    type: string;
    nameZh: string;
    nameVi: string;
    value: string;
    displayOrder: number;
    icon: string;
    isActive: boolean;
    descriptionZh: string;
    descriptionVi: string;
  }>) => 
    apiRequest<{ success: boolean; data: { contactInfo: any } }>(`/contacts/infos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // 删除联系方式（管理员）
  deleteContactInfo: (id: number) => 
    apiRequest<{ success: boolean; message: string }>(`/contacts/infos/${id}`, {
      method: 'DELETE',
    }),
};

// 消息API
export const messageApi = {
  // 提交咨询信息（公开）
  createMessage: (data: {
    name: string;
    email: string;
    phone?: string;
    company?: string;
    subject: string;
    message: string;
  }) =>
    apiRequest<{ success: boolean; message: string; data: { message: any } }>('/contacts/messages', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 获取所有消息
  getMessages: (params?: { status?: string; page?: number; limit?: number }) => 
    apiRequest<{ success: boolean; data: { messages: any[]; pagination: any } }>(
      `/contacts/messages?${new URLSearchParams(params || {}).toString()}`
    ),

  // 标记消息为已读
  markMessageAsRead: (id: number) => 
    apiRequest<{ success: boolean; data: { message: any } }>(`/contacts/messages/${id}/read`, {
      method: 'PATCH',
    }),

  // 回复消息
  respondToMessage: (id: number, replyMessage: string) => 
    apiRequest<{ success: boolean; data: { message: any } }>(`/contacts/messages/${id}/respond`, {
      method: 'POST',
      body: JSON.stringify({ replyMessage }),
    }),

  // 删除消息
  deleteMessage: (id: number) => 
    apiRequest<{ success: boolean; message: string }>(`/contacts/messages/${id}`, {
      method: 'DELETE',
    }),
};

// 项目API
export const projectApi = {
  // 获取所有项目（公开）
  getProjects: (params?: { project_type?: string; is_featured?: boolean; status?: string }) =>
    apiRequest<{ success: boolean; data: any[] }>(
      `/content/projects/all?${new URLSearchParams(
        Object.fromEntries(
          Object.entries(params || {}).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
        )
      ).toString()}`
    ),

  // 获取精选项目
  getFeaturedProjects: (limit = 6) =>
    apiRequest<{ success: boolean; data: any[] }>(`/content/projects/featured?limit=${limit}`),

  // 根据ID获取项目详情
  getProjectById: (id: number) =>
    apiRequest<{ success: boolean; data: any }>(`/content/projects/by-id/${id}`),

  // 根据slug获取项目详情
  getProjectBySlug: (slug: string) =>
    apiRequest<{ success: boolean; data: any }>(`/content/projects/${slug}`),

  // 创建项目（管理员）
  createProject: (data: {
    name_zh: string;
    name_vi: string;
    description_zh: string;
    description_vi: string;
    client_name_zh?: string;
    client_name_vi?: string;
    location_zh?: string;
    location_vi?: string;
    project_type?: string;
    area_size?: number;
    project_value?: number;
    start_date?: string;
    end_date?: string;
    cover_image_url?: string;
    gallery_images?: string[];
    status?: string;
    is_featured?: boolean;
    slug?: string;
  }) =>
    apiRequest<{ success: boolean; data: any }>('/content/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 更新项目（管理员）
  updateProject: (id: number, data: Record<string, any>) =>
    apiRequest<{ success: boolean; data: any }>(`/content/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // 删除项目（管理员）
  deleteProject: (id: number) =>
    apiRequest<{ success: boolean; message: string }>(`/content/projects/${id}`, {
      method: 'DELETE',
    }),
};

// 网站设置API
export const settingsApi = {
  // 获取公开网站设置（logo、公司名等）
  getWebsiteSettings: () =>
    apiRequest<{ success: boolean; data: Record<string, string> }>('/settings/website'),

  // 更新网站设置（管理员）
  updateWebsiteSettings: (data: Record<string, string>) =>
    apiRequest<{ success: boolean; data: Record<string, string> }>('/settings/website', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

export default {
  contactApi,
  settingsApi,
  messageApi,
  projectApi,
};

// 分公司API
export const branchOfficesApi = {
  // 获取所有分公司（公开）
  getAll: (isActive?: boolean) =>
    apiRequest<{ success: boolean; data: any[] }>(
      `/branch-offices${isActive !== undefined ? `?is_active=${isActive}` : ''}`
    ),

  // 获取单个分公司
  getById: (id: number) =>
    apiRequest<{ success: boolean; data: any }>(`/branch-offices/${id}`),

  // 创建分公司（管理员）
  create: (data: {
    city_zh: string;
    city_vi: string;
    address_zh: string;
    address_vi: string;
    phone?: string;
    email?: string;
    sort_order?: number;
    is_active?: boolean;
  }) =>
    apiRequest<{ success: boolean; data: any }>('/branch-offices', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 更新分公司（管理员）
  update: (id: number, data: Partial<{
    city_zh: string;
    city_vi: string;
    address_zh: string;
    address_vi: string;
    phone: string;
    email: string;
    sort_order: number;
    is_active: boolean;
  }>) =>
    apiRequest<{ success: boolean; data: any }>(`/branch-offices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // 删除分公司（管理员）
  delete: (id: number) =>
    apiRequest<{ success: boolean; message: string }>(`/branch-offices/${id}`, {
      method: 'DELETE',
    }),
};

// 服务API
export const servicesApi = {
  // 获取所有服务（公开）
  getAll: () =>
    apiRequest<{ success: boolean; data: any[] }>('/services'),

  // 获取单个服务
  getById: (id: number) =>
    apiRequest<{ success: boolean; data: any }>(`/services/${id}`),

  // 创建服务（管理员）
  create: (data: {
    slug?: string;
    name_zh: string;
    name_vi: string;
    icon_class?: string;
    icon_color?: string;
    description_zh: string;
    description_vi: string;
    image_url?: string;
    sort_order?: number;
    is_active?: boolean;
  }) =>
    apiRequest<{ success: boolean; data: any }>('/services', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 更新服务（管理员）
  update: (id: number, data: Partial<{
    slug: string;
    name_zh: string;
    name_vi: string;
    icon_class: string;
    icon_color: string;
    description_zh: string;
    description_vi: string;
    image_url: string;
    sort_order: number;
    is_active: boolean;
  }>) =>
    apiRequest<{ success: boolean; data: any }>(`/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // 删除服务（管理员）
  delete: (id: number) =>
    apiRequest<{ success: boolean; message: string }>(`/services/${id}`, {
      method: 'DELETE',
    }),
};

// 首页内容API（banners、company_intro、advantages等）
export const homepageContentApi = {
  // 按 section_key 获取内容
  getBySection: (sectionKey: string) =>
    apiRequest<{ success: boolean; data: any[] }>(`/content/banners/all?section_key=${sectionKey}`),

  // 获取所有内容
  getAll: () =>
    apiRequest<{ success: boolean; data: any[] }>('/content/banners/all'),
};